import type { MatrixAnswer, MatrixResult } from '@/types/occurrence'

/** Destino de uma resposta: próxima pergunta e/ou resultado preliminar. */
export interface MatrixOutcome {
	next: string | 'END'
	/** Resultado preliminar (array = investigador escolhe, doc ambíguo) */
	prelim?: MatrixResult | MatrixResult[]
	/** Fator sistêmico a registrar automaticamente */
	factor?: string
}

export interface MatrixQuestion {
	id: string
	text: string
	help: string
	evidence: string
	yes: MatrixOutcome
	no: MatrixOutcome
	allowNA?: boolean
}

export const MATRIX_VERSION = 'MI v1 (mock)'

/** Perguntas MI-01..MI-11 conforme Seção 6 do documento. MI-12 = validação do comitê. */
export const MATRIX_QUESTIONS: MatrixQuestion[] = [
	{
		id: 'MI-01',
		text: 'Existe procedimento, regra ou padrão aplicável, vigente, acessível e suficientemente claro?',
		help: 'Considere a revisão vigente no Portal SIG e se a equipe tinha acesso.',
		evidence: 'Procedimento/revisão, análise de disponibilidade e clareza.',
		yes: { next: 'MI-02' },
		no: { next: 'MI-05', factor: 'Falha sistêmica de procedimento' },
	},
	{
		id: 'MI-02',
		text: 'A pessoa recebeu treinamento válido e compatível com a tarefa e o risco?',
		help: 'Verifique validade, conteúdo e avaliação do treinamento.',
		evidence: 'Registro, data, conteúdo, validade e avaliação do treinamento.',
		yes: { next: 'MI-03' },
		no: { next: 'MI-05', factor: 'Falha de capacitação' },
	},
	{
		id: 'MI-03',
		text: 'A pessoa demonstrava conhecer e compreender a forma correta de executar?',
		help: 'Use depoimento ou demonstração prática.',
		evidence:
			'Depoimento, demonstração prática, avaliação ou evidência equivalente.',
		yes: { next: 'MI-04' },
		no: { next: 'MI-05', factor: 'Lacuna de compreensão/competência' },
	},
	{
		id: 'MI-04',
		text: 'A consequência indesejada era razoavelmente previsível nas condições reais?',
		help: 'Considere APR, alertas, sinalização e experiência prévia.',
		evidence:
			'APR, alertas, sinalização, experiência prévia e condições observadas.',
		yes: { next: 'MI-07' },
		no: { next: 'MI-05' },
	},
	{
		id: 'MI-05',
		text: 'Outra pessoa com capacitação semelhante poderia agir da mesma forma nas mesmas condições?',
		help: 'Teste de substituição: compare com a prática real da equipe.',
		evidence: 'Comparação com trabalho real, práticas da equipe e entrevistas.',
		yes: { next: 'MI-06' },
		no: { next: 'MI-07' },
	},
	{
		id: 'MI-06',
		text: 'Condições do sistema influenciaram a decisão ou tornaram a forma correta difícil?',
		help: 'Pressão de prazo, recursos, layout, ferramentas, fadiga, comunicação, supervisão e metas.',
		evidence:
			'Pressão de prazo, recursos, layout, ferramentas, fadiga, comunicação, supervisão e metas.',
		yes: { next: 'MI-10', prelim: ['ERROR', 'CIRCUMSTANTIAL_VIOLATION'] },
		no: { next: 'MI-10', prelim: 'ERROR' },
	},
	{
		id: 'MI-07',
		text: 'Houve decisão consciente de não seguir a regra conhecida?',
		help: 'Não aceitar conclusão baseada apenas em opinião.',
		evidence: 'Sequência dos fatos, depoimentos e evidências objetivas.',
		yes: { next: 'MI-08' },
		no: { next: 'MI-10', prelim: 'ERROR' },
	},
	{
		id: 'MI-08',
		text: 'A forma adotada era prática tolerada, incentivada ou conhecida pela liderança/equipe?',
		help: 'Verifique supervisão, ocorrências anteriores e práticas usuais.',
		evidence:
			'Registros de supervisão, ocorrências anteriores, comunicações, observações e práticas usuais.',
		yes: {
			next: 'MI-10',
			prelim: 'CIRCUMSTANTIAL_VIOLATION',
			factor: 'Fator de liderança/sistema',
		},
		no: { next: 'MI-09' },
	},
	{
		id: 'MI-09',
		text: 'Existiam condições, recursos, tempo e alternativas viáveis para cumprir a regra?',
		help: 'Considere autorização para parar, planejamento e carga de trabalho.',
		evidence:
			'Disponibilidade de recursos, autorização para parar, planejamento, carga de trabalho e alternativas.',
		yes: { next: 'MI-10', prelim: 'DELIBERATE_VIOLATION' },
		no: {
			next: 'MI-10',
			prelim: 'CIRCUMSTANTIAL_VIOLATION',
			factor: 'Barreiras para cumprir a regra',
		},
	},
	{
		id: 'MI-10',
		text: 'Fatores físicos, fisiológicos, cognitivos, organizacionais ou ambientais contribuíram?',
		help: 'Somente informações pertinentes e autorizadas; dados clínicos permanecem restritos à Medicina.',
		evidence: 'Somente informações pertinentes e autorizadas.',
		yes: {
			next: 'MI-11',
			factor: 'Fator contribuinte (físico/cognitivo/organizacional/ambiental)',
		},
		no: { next: 'MI-11' },
	},
	{
		id: 'MI-11',
		text: 'As respostas e evidências são suficientes e coerentes para concluir?',
		help: 'Resumo do caminho, evidências, divergências e pendências.',
		evidence: 'Resumo do caminho, evidências, divergências e pendências.',
		yes: { next: 'END' },
		no: { next: 'END', prelim: 'INCONCLUSIVE' },
	},
]

export interface MatrixWalk {
	/** Perguntas respondidas na ordem do caminho */
	path: MatrixQuestion[]
	current: MatrixQuestion | null
	factors: string[]
	prelim: MatrixResult | MatrixResult[] | null
	done: boolean
	/** Respostas "órfãs" que não pertencem mais ao caminho (após edição) */
	orphans: string[]
}

const byId = new Map(MATRIX_QUESTIONS.map((q) => [q.id, q]))

/** Percorre o fluxo a partir das respostas; recalcula tudo a cada edição. */
export function walkMatrix(answers: MatrixAnswer[]): MatrixWalk {
	const answerOf = new Map(answers.map((a) => [a.questionId, a]))
	const path: MatrixQuestion[] = []
	const factors: string[] = []
	let prelim: MatrixResult | MatrixResult[] | null = null
	let cursor: string | 'END' = 'MI-01'
	let current: MatrixQuestion | null = null

	while (cursor !== 'END') {
		const q = byId.get(cursor)
		if (!q) break
		const a = answerOf.get(q.id)
		if (!a) {
			current = q
			break
		}
		path.push(q)
		const outcome: MatrixOutcome =
			a.answer === 'YES'
				? q.yes
				: a.answer === 'NO'
					? q.no
					: { next: 'END', prelim: 'INCONCLUSIVE' }
		if (outcome.factor) factors.push(outcome.factor)
		if (outcome.prelim) prelim = outcome.prelim
		cursor = outcome.next
	}

	const pathIds = new Set(path.map((q) => q.id))
	const orphans = answers
		.filter((a) => !pathIds.has(a.questionId))
		.map((a) => a.questionId)
	return {
		path,
		current,
		factors,
		prelim,
		done: cursor === 'END' && !current,
		orphans,
	}
}
